import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock, MapPin, Pause, Play, Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { EmptyState } from "@/components/EmptyState";
import { ModalOverlay } from "@/components/ModalOverlay";
import { PaginatedList } from "@/components/PaginatedList";
import { usePartnerScope } from "@/components/partner-scope-context";
import { usePageTitle } from "@/lib/use-page-title";
import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { usePaginatedList } from "@/lib/pagination";
import { hasWriteOn, isSuperAdmin } from "@/lib/roles";
import { browserTimezone, timezoneChoicesWithBrowser } from "@/lib/timezones";
import { useRoleProfile } from "@/lib/use-role-profile";
import { cn } from "@/lib/utils";
import type { Location, LocationStatus, Organization } from "@tilt/admin-sdk";

export function LocationsPage(): JSX.Element {
  usePageTitle("Locations");
  const queryClient = useQueryClient();
  const { scope } = usePartnerScope();
  // Deep-link: /locations?organization_id=<id> pre-filters the list
  // to that org (used by the "Open" button on the Organizations
  // page). Strip the param after first paint so a refresh doesn't
  // re-pin the filter surprisingly.
  const [searchParams, setSearchParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [statusFilter, setStatusFilter] = useState<"" | LocationStatus>("");
  const [orgFilter, setOrgFilter] = useState<string>(searchParams.get("organization_id") ?? "");

  useEffect(() => {
    if (searchParams.has("organization_id")) {
      const next = new URLSearchParams(searchParams);
      next.delete("organization_id");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch the org picker's options scoped to the active topbar scope.
  // Pagination default is 50 - ask for the max so the dropdown has
  // every org the operator can pick from in one round-trip.
  const orgs = useQuery({
    queryKey: ["organizations", "for-locations", scope.id],
    queryFn: () => apiClient.listOrganizations({ limit: 200, scope_path: scope.path }),
    retry: false,
  });

  const activeOrgs = useMemo(
    () => (orgs.data?.organizations ?? []).filter((o) => !o.is_deleted),
    [orgs.data],
  );

  // Server-side pagination + free-text search. The handler owns
  // ordering (partner_path, org_name, loc_name); all we send is the
  // scope filter + limit/offset/q. ``scope_path`` narrows results
  // to the active topbar scope's subtree so switching Platform →
  // Finterra actually changes what's shown.
  const locsList = usePaginatedList({
    queryKey: ["locations", scope.id, { showDeleted, statusFilter, orgFilter }],
    fetcher: ({ limit, offset, q }) =>
      apiClient
        .listLocations({
          limit,
          offset,
          ...(q ? { q } : {}),
          scope_path: scope.path,
          include_deleted: showDeleted,
          ...(statusFilter ? { status: statusFilter } : {}),
          ...(orgFilter ? { organization_id: orgFilter } : {}),
        })
        .then((res) => ({
          items: res.locations,
          total: res.total,
          limit: res.limit,
          offset: res.offset,
          has_more: res.has_more,
        })),
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["locations"] });
  };

  // "New location" creates under a parent org - require_write on the
  // org's partner path. Hide the button when the caller has no
  // write-capable grant on ANY visible org in their scope.
  const profile = useRoleProfile();
  const canCreate =
    isSuperAdmin(profile) || activeOrgs.some((o) => hasWriteOn(profile, o.partner_path));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">Locations</h1>
          <p className="text-sm text-muted-foreground">
            The stores and sites where payments are taken. Each location belongs to one
            organization.
          </p>
        </div>
        {canCreate && (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="flex flex-shrink-0 items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            <Plus className="h-4 w-4" />
            New location
          </button>
        )}
      </div>

      <PaginatedList
        state={locsList}
        itemNoun="locations"
        searchPlaceholder="Search by name, slug, or organization…"
        filters={
          <>
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              Organization
              <select
                value={orgFilter}
                onChange={(e) => setOrgFilter(e.target.value)}
                className="rounded-md border bg-background px-2 py-1 text-xs"
              >
                <option value="">All in scope</option>
                {activeOrgs.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              Status
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
                className="rounded-md border bg-background px-2 py-1 text-xs"
              >
                <option value="">Any</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={(e) => setShowDeleted(e.target.checked)}
                className="h-3.5 w-3.5 accent-primary"
              />
              Show deleted
            </label>
          </>
        }
        emptyState={
          activeOrgs.length === 0 ? (
            <EmptyState
              icon={MapPin}
              title="No organizations in your scope"
              description="Locations sit under an organization. Create one on the Organizations page, then come back here."
              cta={{ label: "Go to Organizations", href: "/organizations" }}
            />
          ) : (
            <EmptyState
              icon={MapPin}
              title="No locations yet"
              description={
                canCreate
                  ? "Locations are the physical sites running POS terminals. Click “New location” above to add one."
                  : "No locations in your scope yet. Ask an admin with write access to create one."
              }
              {...(canCreate
                ? { cta: { label: "New location", onClick: () => setCreateOpen(true) } }
                : {})}
            />
          )
        }
      >
        {locsList.query.isError && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {locsList.query.error instanceof Error
              ? locsList.query.error.message
              : "Failed to load locations"}
          </div>
        )}
        {locsList.items.length > 0 && (
          <div className="overflow-x-auto rounded-lg border bg-card">
            <table className="w-full text-sm">
              <thead className="sticky top-0 border-b bg-muted/40 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-2.5">Name</th>
                  <th className="px-4 py-2.5">Organization</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5">Vertical</th>
                  <th className="px-4 py-2.5">Timezone</th>
                  <th className="px-4 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {locsList.items.map((l) => (
                  <LocationRow key={l.id} loc={l} onMutated={invalidate} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PaginatedList>

      {createOpen && (
        <CreateDialog
          orgs={activeOrgs}
          defaultOrgId={orgFilter || activeOrgs[0]?.id || ""}
          onClose={() => setCreateOpen(false)}
          onMutated={() => {
            setCreateOpen(false);
            invalidate();
          }}
        />
      )}
    </div>
  );
}

function LocationRow(props: { loc: Location; onMutated: () => void }): JSX.Element {
  const { loc, onMutated } = props;
  const [error, setError] = useState<string | null>(null);
  const profile = useRoleProfile();
  const canWrite = hasWriteOn(profile, loc.partner_path);

  const toggleStatus = useMutation({
    mutationFn: () =>
      apiClient.patchLocation(loc.id, {
        status: loc.status === "active" ? "inactive" : "active",
      }),
    onSuccess: () => {
      setError(null);
      onMutated();
    },
    onError: (err: unknown) => setError(describe(err)),
  });

  const softDelete = useMutation({
    mutationFn: () => apiClient.deleteLocation(loc.id),
    onSuccess: () => {
      setError(null);
      onMutated();
    },
    onError: (err: unknown) => setError(describe(err)),
  });

  return (
    <>
      <tr className="border-b last:border-b-0 hover:bg-muted/30">
        <td className="px-4 py-2.5 align-middle">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
            {loc.is_deleted ? (
              <span className="font-medium">{loc.name}</span>
            ) : (
              <Link
                to={`/locations/${loc.id}`}
                className="font-medium hover:text-primary hover:underline"
              >
                {loc.name}
              </Link>
            )}
            {loc.is_deleted && (
              <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-medium text-destructive">
                deleted
              </span>
            )}
          </div>
          <div className="mt-0.5 break-all font-mono text-[11px] text-muted-foreground">
            {loc.slug}
          </div>
        </td>
        <td className="px-4 py-2.5 align-middle text-muted-foreground">
          {loc.organization_name ?? "-"}
        </td>
        <td className="px-4 py-2.5 align-middle">
          <span
            className={cn(
              "rounded-full px-2 py-0.5 text-[10px] font-medium",
              loc.status === "active"
                ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100"
                : "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100",
            )}
          >
            {loc.status}
          </span>
        </td>
        <td className="px-4 py-2.5 align-middle text-xs text-muted-foreground">
          {loc.effective_vertical_display_name ??
            loc.effective_vertical_slug ??
            loc.vertical_display_name ??
            loc.vertical_slug ??
            "-"}
          {loc.inherits_vertical && (
            <span className="ml-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
              inherited
            </span>
          )}
        </td>
        <td className="px-4 py-2.5 align-middle">
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" aria-hidden="true" />
            {loc.timezone}
          </span>
        </td>
        <td className="px-4 py-2.5 align-middle">
          <div className="flex items-center justify-end gap-2">
            {!loc.is_deleted && (
              <>
                {canWrite && (
                  <>
                    <button
                      type="button"
                      disabled={toggleStatus.isPending}
                      onClick={() => toggleStatus.mutate()}
                      aria-label={
                        loc.status === "active" ? "Deactivate location" : "Activate location"
                      }
                      title={loc.status === "active" ? "Deactivate" : "Activate"}
                      className="inline-flex items-center gap-1 rounded-md border bg-background px-2 py-1 text-xs hover:bg-muted disabled:opacity-50"
                    >
                      {loc.status === "active" ? (
                        <Pause className="h-3.5 w-3.5" aria-hidden="true" />
                      ) : (
                        <Play className="h-3.5 w-3.5" aria-hidden="true" />
                      )}
                      <span className="hidden sm:inline">
                        {loc.status === "active" ? "Deactivate" : "Activate"}
                      </span>
                    </button>
                    <button
                      type="button"
                      disabled={softDelete.isPending}
                      onClick={() => {
                        if (!confirm(`Soft-delete location "${loc.name}"?`)) return;
                        softDelete.mutate();
                      }}
                      aria-label={`Delete ${loc.name}`}
                      className="inline-flex items-center gap-1 rounded-md border border-destructive/40 bg-destructive/5 px-2 py-1 text-xs text-destructive hover:bg-destructive/10 disabled:opacity-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                      <span className="hidden sm:inline">Delete</span>
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </td>
      </tr>
      {error && (
        <tr>
          <td colSpan={6} className="px-4 py-2">
            <div className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">{error}</div>
          </td>
        </tr>
      )}
    </>
  );
}

function CreateDialog(props: {
  orgs: Organization[];
  defaultOrgId: string;
  onClose: () => void;
  onMutated: () => void;
}): JSX.Element {
  const [organizationId, setOrganizationId] = useState(props.defaultOrgId);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugManual, setSlugManual] = useState(false);
  // Vertical options are scoped to the parent organization's enabled
  // set - refetches when the user switches the Organization dropdown.
  // The backend validates the vertical against the org's enabled list,
  // so this keeps the UI consistent with what's accepted.
  const orgVerticalsQuery = useQuery({
    queryKey: ["organization-verticals", organizationId],
    queryFn: () => apiClient.listOrganizationVerticals(organizationId),
    enabled: !!organizationId,
    staleTime: 60_000,
  });
  const orgVerticals = useMemo(
    () => orgVerticalsQuery.data?.verticals ?? [],
    [orgVerticalsQuery.data],
  );
  const [verticalId, setVerticalId] = useState<string>("");
  // Default to the org's default vertical when the list arrives; reset
  // whenever the user picks a different org.
  useEffect(() => {
    if (orgVerticals.length === 0) {
      if (verticalId) setVerticalId("");
      return;
    }
    if (!orgVerticals.some((v) => v.vertical_id === verticalId)) {
      const preferred = orgVerticals.find((v) => v.is_default) ?? orgVerticals[0];
      setVerticalId(preferred.vertical_id);
    }
  }, [orgVerticals, verticalId]);
  const [seedDepartments, setSeedDepartments] = useState(true);
  // Default new-location timezone to the creator's browser zone -
  // admin spinning up a location is almost always in (or near) the
  // location's own TZ, and a wrong default surfaces only after the
  // location has already been saved. UTC fallback when ``Intl`` is
  // unavailable. See ``lib/timezones`` for the curated dropdown set.
  const [timezone, setTimezone] = useState(() => browserTimezone());
  const [salesTaxPct, setSalesTaxPct] = useState("");
  const [error, setError] = useState<string | null>(null);

  const onNameChange = (value: string): void => {
    setName(value);
    if (!slugManual) {
      setSlug(
        value
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9-]+/g, "-")
          .replace(/^-+|-+$/g, "")
          .slice(0, 30),
      );
    }
  };

  const create = useMutation({
    mutationFn: () =>
      apiClient.createLocation({
        organization_id: organizationId,
        name: name.trim(),
        slug: slug.trim(),
        ...(verticalId ? { vertical_id: verticalId } : {}),
        seed_departments_from_vertical: seedDepartments,
        ...(timezone.trim() ? { timezone: timezone.trim() } : {}),
        ...(salesTaxPct.trim() ? { sales_tax_rate: (Number(salesTaxPct) / 100).toFixed(4) } : {}),
      }),
    onSuccess: () => {
      setError(null);
      props.onMutated();
    },
    onError: (err: unknown) => setError(describe(err)),
  });

  // Empty-orgs fallback - a location needs an organization, so if
  // there are none we surface a route to the Organizations page
  // rather than a form the user can't submit.
  if (props.orgs.length === 0) {
    return (
      <ModalOverlay ariaLabel="Create location" onClose={props.onClose}>
        <div className="w-full max-w-md space-y-3 rounded-t-xl border bg-card p-5 shadow-lg sm:rounded-xl">
          <h2 className="text-sm font-semibold">New location</h2>
          <EmptyState
            icon={MapPin}
            title="No organizations in your scope"
            description="A location must sit under an organization. Create one on the Organizations page first, then come back here."
            cta={{ label: "Go to Organizations", href: "/organizations" }}
            secondaryCta={{ label: "Close", onClick: props.onClose }}
          />
        </div>
      </ModalOverlay>
    );
  }

  return (
    <ModalOverlay ariaLabel="Create location" onClose={props.onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
        className="w-full max-w-md space-y-3 rounded-t-xl border bg-card p-4 shadow-lg sm:rounded-xl sm:p-5"
      >
        <h2 className="text-sm font-semibold">New location</h2>

        <div>
          <label htmlFor="l-org" className="mb-1 block text-xs font-medium text-muted-foreground">
            Organization
          </label>
          <select
            id="l-org"
            required
            value={organizationId}
            onChange={(e) => setOrganizationId(e.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          >
            {props.orgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="l-name" className="mb-1 block text-xs font-medium text-muted-foreground">
            Name
          </label>
          <input
            id="l-name"
            type="text"
            required
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            placeholder="Downtown"
          />
        </div>

        <div>
          <label htmlFor="l-slug" className="mb-1 block text-xs font-medium text-muted-foreground">
            Slug <span className="text-muted-foreground/80">(a-z, 0-9, hyphens; 3-30 chars)</span>
          </label>
          <input
            id="l-slug"
            type="text"
            required
            spellCheck={false}
            value={slug}
            onChange={(e) => {
              setSlugManual(true);
              setSlug(e.target.value);
            }}
            pattern="[a-z][a-z0-9-]{2,29}"
            className="w-full rounded-md border bg-background px-3 py-2 font-mono text-sm"
            placeholder="acme-downtown"
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label
              htmlFor="l-vert"
              className="mb-1 block text-xs font-medium text-muted-foreground"
            >
              Vertical
            </label>
            <select
              id="l-vert"
              value={verticalId}
              onChange={(e) => setVerticalId(e.target.value)}
              disabled={orgVerticals.length === 0}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm disabled:opacity-60"
            >
              {orgVerticalsQuery.isLoading && <option value="">Loading…</option>}
              {orgVerticals.map((v) => (
                <option key={v.vertical_id} value={v.vertical_id}>
                  {v.vertical_display_name}
                  {v.is_default ? " (default)" : ""}
                </option>
              ))}
            </select>
            {!orgVerticalsQuery.isLoading && organizationId && orgVerticals.length === 0 && (
              <p className="mt-1 text-[11px] text-muted-foreground">
                The selected organization has no enabled verticals. Add one on its Verticals tab
                first.
              </p>
            )}
            <label className="mt-1 flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <input
                type="checkbox"
                checked={seedDepartments}
                onChange={(e) => setSeedDepartments(e.target.checked)}
                className="h-3 w-3 accent-primary"
              />
              Seed default departments from this vertical
            </label>
          </div>
          <div>
            <label htmlFor="l-tz" className="mb-1 block text-xs font-medium text-muted-foreground">
              Timezone
            </label>
            <select
              id="l-tz"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            >
              {timezoneChoicesWithBrowser().map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="l-tax" className="mb-1 block text-xs font-medium text-muted-foreground">
            Sales tax <span className="text-muted-foreground/80">(percent; optional)</span>
          </label>
          <input
            id="l-tax"
            type="number"
            inputMode="decimal"
            min="0"
            max="99.99"
            step="0.01"
            value={salesTaxPct}
            onChange={(e) => setSalesTaxPct(e.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            placeholder="8.75"
          />
        </div>

        {error && (
          <div className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">{error}</div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={props.onClose}
            className="rounded-md border px-3 py-1.5 text-xs"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={create.isPending || !organizationId || !name.trim() || !slug.trim()}
            className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {create.isPending ? "Creating…" : "Create"}
          </button>
        </div>
      </form>
    </ModalOverlay>
  );
}
